/**
 * The Tables tab's sheets, named after the client's own brief so they
 * recognise their schema: Rent & expenses (the P&L grid), Property, Capital,
 * Owners and Loans. Each sheet is our version of one of their sheets, with
 * every cell still pointing at the workbook cell it was read from.
 */

import type {
	Portfolio,
	PortfolioHouse,
	PropertyField,
} from "../../services/portfolioDataService";

export const SHEETS = [
	{ id: "pl", label: "Rent & expenses" },
	{ id: "property", label: "Property" },
	{ id: "capital", label: "Capital" },
	{ id: "owners", label: "Owners" },
	{ id: "loans", label: "Loans" },
] as const;

export type SheetId = (typeof SHEETS)[number]["id"];

export const isSheet = (value: string | null): value is SheetId =>
	SHEETS.some((sheet) => sheet.id === value);

/** A cell the side panel shows that is not a P&L figure. */
export interface RecordSelection {
	/** Which sheet and cell, so the table can mark it. */
	key: string;
	houseId: string | null;
	title: string;
	value: string;
	/** "Report!T19" style, with the file to open it in. */
	sourceFile: string;
	sheet: string;
	cell: string;
	/** The column header as exported. */
	header?: string;
	rows?: { label: string; value: string; tone?: "attention" }[];
	note?: string;
}

/** "Finsh_SqFt" → "Finsh SqFt": their header, readable, still recognisable. */
export const headerLabel = (header: string): string =>
	header.replace(/_/g, " ").replace(/\s+/g, " ").trim();

/** "hot_water_tank" → "Hot water tank". */
export const componentLabel = (key: string): string => {
	const text = key.replace(/_/g, " ");
	return text.charAt(0).toUpperCase() + text.slice(1);
};

export function formatField(value: unknown, key = ""): string {
	if (value === null || value === undefined || value === "") return "";
	// A year is a label, not a quantity: 2003, never 2,003.
	if (key.includes("year")) return String(value).replace(/\.0+$/, "");
	if (typeof value === "number") {
		if (key === "ownership_percent") return `${(value * 100).toFixed(1)}%`;
		return Number.isInteger(value)
			? value.toLocaleString("en-US")
			: value.toLocaleString("en-US", { maximumFractionDigits: 2 });
	}
	const text = String(value);
	// Numbers read back from the CSV as text (the house table keeps most
	// export fields as written).
	if (/^-?\d+(\.\d+)?$/.test(text) && !/^0\d/.test(text) && text.length < 12) {
		const number = Number(text);
		return Number.isInteger(number)
			? number.toLocaleString("en-US")
			: number.toLocaleString("en-US", { maximumFractionDigits: 2 });
	}
	return text;
}

export const sourceRowOf = (house: PortfolioHouse): number | null => {
	const row = Number(house.source_row);
	return Number.isFinite(row) && row > 0 ? row : null;
};

/** The export fields a sheet shows: the ones some house fills. */
export function shownFields(
	portfolio: Portfolio,
	groups: readonly PropertyField["group"][],
): { shown: PropertyField[]; hidden: PropertyField[] } {
	const fields = (portfolio.manifest.property_sheet?.fields ?? []).filter((field) =>
		groups.includes(field.group),
	);
	return {
		shown: fields.filter((field) => field.filled),
		hidden: fields.filter((field) => !field.filled),
	};
}
